import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useHandoffs } from "../lib/tenant-api";
import { apiFetch } from "../lib/api";
import { formatRelative } from "../lib/format";
import {
  EmptyState,
  ErrorBanner,
  LoadingRow,
} from "../components/EmptyState";

export default function Handoffs() {
  const { data, isLoading, error } = useHandoffs();
  const qc = useQueryClient();
  const resolve = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/tenant/handoffs/${id}/resolve`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", "handoffs"] });
      qc.invalidateQueries({ queryKey: ["tenant", "overview"] });
    },
  });

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">Handoffs</h1>
        <p className="mt-1 text-sm text-slate-500">
          Conversations the AI escalated to a human. Mark resolved once you've
          followed up with the caller.
        </p>
      </div>
      {isLoading && <LoadingRow />}
      {error && <ErrorBanner message={(error as Error).message} />}
      {data && data.length === 0 && (
        <EmptyState
          icon="🚨"
          title="No handoffs"
          hint="Sofia escalates conversations only when there's a real reason — that's a good thing."
        />
      )}
      {data && data.length > 0 && (
        <div className="space-y-3">
          {data.map((h) => {
            const pending = h.status === "pending";
            return (
              <div
                key={h.id}
                className={`rounded-xl border p-4 ${
                  pending ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <UrgencyBadge urgency={h.urgency} />
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          pending
                            ? "bg-amber-200 text-amber-900"
                            : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {h.status}
                      </span>
                      <span className="text-xs text-slate-500">
                        {formatRelative(h.created_at)}
                      </span>
                    </div>
                    <div className="mt-2 font-medium text-slate-900">
                      {h.reason}
                    </div>
                    {h.summary && (
                      <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">
                        {h.summary}
                      </p>
                    )}
                  </div>
                  {pending && (
                    <button
                      onClick={() => resolve.mutate(h.id)}
                      disabled={resolve.isPending}
                      className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition"
                    >
                      Mark resolved
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function UrgencyBadge({ urgency }: { urgency: string }) {
  const map: Record<string, string> = {
    emergency: "bg-red-100 text-red-700",
    high: "bg-orange-100 text-orange-700",
    normal: "bg-slate-100 text-slate-700",
    low: "bg-slate-100 text-slate-500",
  };
  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded ${map[urgency] ?? map.normal}`}
    >
      {urgency}
    </span>
  );
}
